<template>
  <div>
    <v-btn
      :color="color"
      :data-cy="`deleteBtn--${fkEntityName}${fkEntityId}`"
      :disabled="disabled"
      :outlined="outlined"
      @click="openDialog()"
      elevation="0"
      small
      :text="text"
      >Delete</v-btn
    >
    <v-dialog
      v-model="dialog"
      :width="impactedEntityList.length ? '' : '550px'"
    >
      <v-card :loading="loading">
        <v-toolbar class="pa-0" elevation="0">
          <h2 class="text-center pt-1" data-cy="deleteDialogHeader">
            {{
              impactedEntityList.length
                ? `Cannot Delete ${fkEntityLabel}`
                : "Delete Confirmation"
            }}
          </h2>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog = false">
            <v-icon small>fas fa-times</v-icon>
          </v-btn>
        </v-toolbar>
        <v-divider></v-divider>
        <template v-if="loading">
          <v-card-text>
            <GenericLoader>Loading impacted entities...</GenericLoader>
          </v-card-text>
        </template>
        <template v-else>
          <template v-if="impactedEntityList.length">
            <v-card-text class="mt-4 pb-1">
              <p class="title">
                {{ impactedEntityList.length }}
                {{
                  impactedEntityList.length == 1
                    ? "entity references"
                    : "entities reference"
                }}
                {{ fkEntityLabel }} in their attributes:
              </p>
              <v-card outlined>
                <v-data-table
                  :headers="headers"
                  :items="impactedEntityList"
                  disable-pagination
                  fixed-header
                  height="400"
                  hide-default-footer
                >
                  <template v-slot:item.impactedAttributeList="{ item }">{{
                    Array.isArray(item.impactedAttributeList)
                      ? item.impactedAttributeList
                          .map(({ label }) => label)
                          .join(", ")
                      : item.impactedAttributeList
                  }}</template>
                </v-data-table>
              </v-card>
              <p class="title mt-4 mb-0">
                These values must be changed before {{ fkEntityLabel }} can be
                deleted.
              </p>
            </v-card-text>
            <v-card-actions class="pl-4 pb-6">
              <template v-for="entityName in impactedEntityNameList">
                <v-btn
                  :key="entityName"
                  @click="openBatchEdit(entityName)"
                  color="primary"
                  class="ml-2"
                  outlined
                  small
                  v-if="
                    impactedEntityList.find(
                      entity => entity.entityName === entityName
                    )
                  "
                  >Batch Edit {{ entityName }}(s)</v-btn
                >
              </template>
            </v-card-actions>
          </template>
          <template v-else>
            <v-card-text class="mt-4">
              <p class="title text-center">
                Are you sure you want to delete {{ fkEntityLabel }}?
              </p>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                @click="removeEntity"
                data-cy="deleteConfirmationBtn"
                class="mr-2 white--text"
                color="warning"
              >
                Delete {{ fkEntityLabel }}
              </v-btn>
            </v-card-actions>
          </template>
        </template></v-card
      ></v-dialog
    >
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { toBase64 } from "@/lib/stringUtil";
import { upperFirst } from "lodash";
import GenericLoader from "@/components/GenericLoader";

export default {
  components: {
    GenericLoader,
  },
  computed: {
    impactedAttributeListByEntityName() {
      const attributeList = {};

      this.impactedEntityNameList.forEach(
        entityName =>
          (attributeList[entityName] = [].concat(
            this.attributeFilter({
              entityName: entityName,
              fkName: this.fkEntityName,
            })
          ))
      );

      return attributeList;
    },
    impactedEntityNameList() {
      return Object.keys(this.impactedEntityConfig);
    },
    ...mapGetters({
      attributeFilter: "attribute/filter",
    }),
  },
  data() {
    return {
      dialog: false,
      headers: [
        { text: "Entity Type", value: "entityName" },
        { text: "Import Key", value: "importKey" },
        {
          text: `Attributes using ${this.fkEntityLabel}`,
          value: "impactedAttributeList",
        },
      ],
      impactedEntityList: [],
      loading: false,
    };
  },
  methods: {
    upperFirst,
    checkEmptyArray(varToCheck) {
      // I got tired of making typing the same if-statement over and over while making this component.
      return !varToCheck || (Array.isArray(varToCheck) && !varToCheck.length);
    },
    openBatchEdit(entityName) {
      const {
        checkEmptyArray,
        impactedEntityConfig,
        impactedEntityList,
        $router,
      } = this;
      const primaryKeyName = impactedEntityConfig[entityName].primaryKey;

      if (!primaryKeyName) {
        return;
      }

      const editEntityList = impactedEntityList.filter(
        entity => entity.entityName === entityName
      );
      if (checkEmptyArray(editEntityList)) {
        return;
      }

      const editAttributeList = impactedEntityList.reduce((acc, entity) => {
        if (
          !entity.impactedAttributeList ||
          !entity.impactedAttributeList.length
        ) {
          return acc;
        }

        acc = acc.concat(entity.impactedAttributeList.map(({ name }) => name));
        return acc;
      }, []); // Create an array of all the attributeNames that need batch editing.

      const idListString = editEntityList
        .map(entity => entity[primaryKeyName])
        .join(",");
      const idListEncoded = toBase64(idListString);

      $router.push({
        name: `${entityName}EditBatch`,
        params: { idListEncoded },
        query: {
          redirect: this.$route.name,
          attributeNameList: JSON.stringify(editAttributeList),
        },
      });
    },
    async openDialog() {
      const { impactedEntityNameList, fkEntityName } = this;
      this.dialog = true;
      this.loading = true;

      try {
        await this.loadAttributeList({ reload: false });
        await this.loadImpactedEntityList();
      } catch (e) {
        console.error(e);

        this.$notifyError({
          message: `Could not load list of ${impactedEntityNameList.join(
            "(s), or "
          )}(s) possibly impacted by deleting this ${fkEntityName}.`,
        });

        // Still allowing a deletion when "impact measurement" fails is safe for admin, but less so for build.
        // Thus, if a GET of the impacted entity list fails, close the dialog.
        this.dialog = false;
        this.loading = false;
      }

      this.loading = false;
    },
    async removeEntity() {
      const { fkEntityName, fkEntityId } = this;
      this.loading = true;

      try {
        this.$store.dispatch(`${fkEntityName}/remove`, fkEntityId);
      } catch (e) {
        console.error(e);

        this.$notifyError({ message: `Could not delete ${fkEntityName}.` });
        this.loading = false;
        return;
      }
      this.$notifySuccess({ message: `${upperFirst(fkEntityName)} deleted.` });

      this.loading = false;
    },
    async loadImpactedEntityList() {
      const { impactedEntityNameList, impactedEntityConfig, fkEntityId } = this;

      const impactedEntityList = [];
      this.impactedEntityList = [];

      for (const entityName of impactedEntityNameList) {
        // For used instead of forEach due to using await.
        const impactedAttributeList = this.impactedAttributeListByEntityName[
          entityName
        ];
        if (this.checkEmptyArray(impactedAttributeList)) {
          continue;
        }

        await this.$store.dispatch(`${entityName}/list`);
        const entityList = this.$store.state[entityName].list;
        if (this.checkEmptyArray(entityList)) {
          continue;
        }

        // For each entity, loop through the list of its impacted attributes,
        // and filter out entities that have undefined, null, or empty arrays saved to those attributes. (nothing gets returned in the map)
        // If they do have values saved to that attribute, save that attribute to the entity's impactedAttributeList.
        const impactedEntityListSingle = entityList.reduce((acc, entity) => {
          entity.entityName = entityName;
          entity.importKey = entity[impactedEntityConfig[entityName].importKey];
          let hasValue = false;
          if (!entity.impactedAttributeList) {
            entity.impactedAttributeList = [];
          }

          for (const attribute of impactedAttributeList) {
            const attributeValue = entity[attribute.name];
            if (
              (Array.isArray(attributeValue) &&
                attributeValue.includes(fkEntityId)) ||
              (!Array.isArray(attributeValue) && attributeValue === fkEntityId) // == instead of === checks both null AND undefined
            ) {
              entity.impactedAttributeList.push(attribute);
              hasValue = true;
            }
          }

          if (hasValue) {
            acc.push(entity);
          }
          return acc;
        }, []);

        /* At this point, impactedEntityListSingle looks like an array of entities, like so:
        [
          {
            firstName, etc: ...
            entityName: "user"
            importKey: "a@b.org"
            impactedAttributeList: [
              {},{}
            ]
          }
        ]*/

        impactedEntityList.push(...impactedEntityListSingle);
      }

      if (this.checkEmptyArray(impactedEntityList)) {
        return;
      }

      this.impactedEntityList = impactedEntityList;
    },

    ...mapActions({
      loadAttributeList: "attribute/list",
    }),
  },
  props: {
    color: {
      default: "primary",
      required: false,
      type: String,
    },
    disabled: {
      default: false,
      required: false,
      type: Boolean,
    },
    fkEntityId: {
      // ex. 2
      // Type not specified as some entities have non-int IDs
      required: true,
    },
    fkEntityLabel: {
      // ex. "Department"
      type: String,
      required: true,
    },
    fkEntityName: {
      // ex. "department"
      type: String,
      required: true,
    },
    impactedEntityConfig: {
      type: Object,
      default: () => {
        return {
          user: { importKey: "email", primaryKey: "userId" },
          resource: { importKey: "label", primaryKey: "id" },
        };
      },
    },
    outlined: {
      default: true,
      required: false,
      type: Boolean,
    },
    text: {
      default: false,
      required: false,
      type: Boolean,
    },
  },
};
</script>
